import { useQuery } from '@tanstack/react-query'
import { createRootRoute, HeadContent, useNavigate, useRouterState } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { type ReactNode, useEffect, useState } from 'react'
import { type Session, sessionQueryOptions, useDeviceTimezoneSync } from '@/entities/session'
import {
  autoRedirectToLogin,
  clearLoginAttempt,
  hasLoginAttempt,
  redirectToLogin,
  UNAUTHORIZED_EVENT,
} from '@/shared/api'
import { BrandLogo } from '@/shared/ui/brand'
import { Button } from '@/shared/ui/button'
import { Spinner } from '@/shared/ui/spinner'
import { AppShell } from '@/widgets/app-shell'
import { CommandPalette } from '@/widgets/command-palette'

// Дефолтный title; разделы уточняют его через head: на своих роутах
// (формат «<Раздел> · STIT Task»), рендерит <HeadContent />.
export const Route = createRootRoute({
  head: () => ({ meta: [{ title: 'STIT Task' }] }),
  component: RootLayout,
})

// Витрина заказчика живёт под /my — внутренние разделы трекера ему не показываем.
// Дом — проекты: клиент приходит за состоянием своего проекта, а плоский список
// задач нужен ему реже.
const CUSTOMER_HOME = '/my/projects'

const isCustomerRoute = (pathname: string): boolean => pathname.startsWith('/my')

// Брендированный полноэкранный статус — переходные/терминальные состояния входа.
function AuthScreen({ message, children }: { message: string; children?: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-bg-base px-4 py-12 text-text-primary">
      <div className="flex flex-col items-center gap-4 text-center">
        <BrandLogo className="h-7" />
        <div>
          <h1 className="t-h3">STIT Task</h1>
          <p className="mt-1 text-sm text-text-tertiary">Внутренний трекер студии</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="text-sm text-text-secondary">{message}</p>
        {children}
      </div>
    </main>
  )
}

// Контур решает, какое приложение перед человеком: заказчик у внутреннего раздела
// (и сотрудник у витрины) не получает отказ, а уезжает к себе домой — разные
// аудитории видят разные приложения, а не ошибку доступа.
function useContourRouting(session: Session | null) {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  useEffect(() => {
    if (!session) return
    const onCustomerRoute = isCustomerRoute(pathname)
    if (session.kind === 'customer' && !onCustomerRoute) {
      void navigate({ to: CUSTOMER_HOME, replace: true })
      return
    }
    if (session.kind === 'employee' && onCustomerRoute) void navigate({ to: '/', replace: true })
  }, [session, pathname, navigate])
}

// Гейт всей аутентифицированной зоны: контур сессии из /api/auth/me. Нет сессии
// (401) → сразу уводим на единый вход хаба (22-unified-login): своей формы логина
// у продукта нет, а по 401 он не знает, сотрудник перед ним или заказчик.
// Вернулись оттуда всё ещё без сессии → экран входа (autoRedirectToLogin рвёт
// цикл). Прочая ошибка (сеть/бэкенд) — экран «Повторить». Гейт живёт в __root —
// каркас не мигает.
function AuthGate({ children }: { children: (session: Session) => ReactNode }) {
  const me = useQuery({ ...sessionQueryOptions, retry: false })
  // 401 может прийти и mid-session из любого API-вызова (event из client.ts) —
  // продление уже отказало, работать нечем: тот же путь на вход.
  const [sessionLost, setSessionLost] = useState(false)
  useEffect(() => {
    const onUnauthorized = () => setSessionLost(true)
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])
  const unauthorized = sessionLost || (me.isSuccess && me.data === null)

  // Попытка уже была → мы вернулись со входа без сессии: показываем экран входа, а
  // autoRedirectToLogin в эффекте на этот раз никуда не уведёт.
  const loginFailed = unauthorized && hasLoginAttempt()
  useEffect(() => {
    if (unauthorized) autoRedirectToLogin()
  }, [unauthorized])
  // Сессия жива — следующий её обрыв снова имеет право на автопереход.
  useEffect(() => {
    if (me.data) clearLoginAttempt()
  }, [me.data])

  useContourRouting(me.data ?? null)
  useDeviceTimezoneSync(me.data ?? null)

  if (unauthorized) {
    return loginFailed ? (
      <AuthScreen message="Вход не завершился — сессия так и не установилась. Попробуйте ещё раз.">
        <Button
          onClick={() => {
            clearLoginAttempt()
            redirectToLogin()
          }}
        >
          Войти
        </Button>
      </AuthScreen>
    ) : (
      <AuthScreen message="Перенаправляем на вход hub.stit.tech…">
        <Spinner label="Перенаправляем на вход" />
      </AuthScreen>
    )
  }

  if (me.isPending) {
    return (
      <AuthScreen message="Проверяем сессию…">
        <Spinner label="Проверяем сессию" />
      </AuthScreen>
    )
  }

  if (me.isError) {
    return (
      <AuthScreen message="Не удалось проверить сессию. Проверьте сеть и попробуйте ещё раз.">
        <Button onClick={() => window.location.reload()}>Повторить</Button>
      </AuthScreen>
    )
  }

  return me.data ? children(me.data) : null
}

// Overlay-виджет (⌘K-палитра) монтируется здесь (page→widgets), а не в AppShell:
// widget→widget запрещён. Открытие — из шапки AppShell пропом. У заказчика палитры
// нет вовсе: поиск в customer-режиме — осознанная не-цель фазы.
function RootLayout() {
  const [searchOpen, setSearchOpen] = useState(false)
  return (
    <>
      <HeadContent />
      <AuthGate>
        {(session) => (
          <>
            <AppShell onOpenSearch={() => setSearchOpen(true)} />
            {session.kind === 'employee' && (
              <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
            )}
          </>
        )}
      </AuthGate>
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </>
  )
}
